import { useEffect, useState } from "react";
import { Loader2, Euro, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

function statusMeta(o, t) {
  if (o.payment_status === "paid") return { label: t("orders.sPaid"), cls: "text-emerald-300 border-emerald-500/40" };
  if (o.status === "expired") return { label: t("orders.sExpired"), cls: "text-red-300 border-red-500/40" };
  if (o.payment_status === "initiated") return { label: t("orders.sInitiated"), cls: "text-zinc-300 border-zinc-700" };
  return { label: t("orders.sOpen"), cls: "text-amber-300 border-amber-500/40" };
}

export function OrdersTable() {
  const { t } = useLang();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const paid = orders.filter((o) => o.payment_status === "paid");
  const revenue = paid.reduce((s, o) => s + (o.amount || 0), 0);

  return (
    <div data-testid="orders-tab">
      <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
        <div className="rounded-xl border border-aura-border bg-aura-surface p-5">
          <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-[0.1em] text-zinc-500">{t("orders.revenue")}</span><Euro size={15} className="text-zinc-600" /></div>
          <div className="text-2xl font-display font-bold text-white">€{revenue.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-aura-border bg-aura-surface p-5">
          <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-[0.1em] text-zinc-500">{t("orders.count")}</span><ShoppingBag size={15} className="text-zinc-600" /></div>
          <div className="text-2xl font-display font-bold text-white">{paid.length}</div>
        </div>
      </div>

      <div className="rounded-xl border border-aura-border bg-aura-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-start text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3 font-medium text-start">{t("orders.colDate")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("orders.colProduct")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("orders.colAmount")}</th>
                <th className="px-4 py-3 font-medium text-start">{t("orders.colStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-16 text-center text-zinc-500"><Loader2 className="animate-spin inline" /></td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-16 text-center text-zinc-500">{t("orders.empty")}</td></tr>
              ) : orders.map((o) => {
                const m = statusMeta(o, t);
                return (
                  <tr key={o.session_id} data-testid={`order-row-${o.session_id}`} className="border-b border-white/5">
                    <td className="px-4 py-4 text-zinc-400">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-4 text-white">{o.product}</td>
                    <td className="px-4 py-4 text-zinc-300">€{(o.amount || 0).toLocaleString()}</td>
                    <td className="px-4 py-4"><span className={`inline-flex items-center rounded-full border ${m.cls} bg-white/[0.03] px-3 py-1 text-xs font-medium`}>{m.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
